import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  Save, 
  X, 
  Target, 
  Calendar,
  User,
  Building,
  DollarSign,
  Star,
  FileText,
  
  Users,
  Award,
  Paperclip,
  Plus,
  RefreshCw,
  Search,
  Check,
  Link,
  Tag,
  HelpCircle
} from 'lucide-react';
import AttachmentServiceV2 from '../../components/Attachments/AttachmentServiceV2';
import TagService from '../../components/Tags/TagService';
import CustomerSelectionPopup from '../../components/Forms/CustomerSelectionPopup';
import entityRelationService from '../../services/entityRelationService';
import { api } from '../../utils/api';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import EntitySelectionModal from '../../components/EntityManagement/EntitySelectionModal';
import CustomAttributesEditor from '../../components/CustomAttributes/CustomAttributesEditor';
import {
  extractCustomAttributeFields,
  sanitizeAttributePayload
} from '../../components/CustomAttributes/utils';
import { clearConfigCache, loadEntityConfig } from '../../utils/entityConfig';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import dataGovernanceService from '../../services/dataGovernanceService';
import { AlertTriangle } from 'lucide-react';
import HelpModal from '../../components/Help/HelpModal';
import { leadHelpContent } from '../../components/Help/helpContent/leads';
import {
  ValidationSummary,
  FieldError,
  FormLabel,
  getInputClassName,
  getSelectClassName,
  getTextareaClassName,
  isEmpty,
  isValidEmail,
  normalizeEmailList,
  isValidUrl,
  isPositiveNumber,
  isInRange,
  scrollToFirstError,
  ValidationMessages
} from '../../components/Forms/FormValidation';
import AutocompleteTextField from '../../components/Forms/AutocompleteTextField';
import { getSuggestionConfig } from '../../components/Forms/TextFieldWithSuggestions';

const parseAttributes = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {};
    } catch (error) {
      console.warn('LeadForm: Failed to parse attributes JSON', error);
      return {};
    }
  }
  return {};
};

// Helper function to convert UTC date to local date string for HTML date input
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    // Create a Date object from the UTC string
    const date = new Date(dateString);
    // Get the local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Return in YYYY-MM-DD format for HTML date input
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn('LeadForm: Failed to format date', error);
    return '';
  }
};

// Helper functions for number formatting in inputs
const formatNumberWithCommas = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  
  // Handle string values (preserving decimals during typing)
  if (typeof value === 'string') {
    const cleanedForParsing = value.replace(/,/g, '');
    
    // If ends with decimal point, format integer part and preserve the decimal
    if (cleanedForParsing.endsWith('.')) {
      const intPart = parseInt(cleanedForParsing.slice(0, -1), 10);
      if (isNaN(intPart)) return value;
      return new Intl.NumberFormat('en-US', { useGrouping: true }).format(intPart) + '.';
    }
    
    // If has decimal, format preserving decimal places (up to 2)
    if (cleanedForParsing.includes('.')) {
      const numValue = parseFloat(cleanedForParsing);
      if (isNaN(numValue)) return value;
      const decimalPart = cleanedForParsing.split('.')[1] || '';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: Math.min(decimalPart.length, 2),
        maximumFractionDigits: 2,
        useGrouping: true
      }).format(numValue);
    }
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(numValue)) return value;
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    useGrouping: true
  }).format(numValue);
};

const parseNumberFromFormatted = (value) => {
  if (!value || value === '') return '';
  // Remove all commas, spaces, and other formatting characters, then parse
  const cleaned = String(value).trim().replace(/[,\s]/g, '');
  if (cleaned === '') return '';
  
  // Preserve trailing decimal point or incomplete decimals during typing
  // This allows users to type "1234." and then continue with decimal digits
  if (/^-?\d+\.$/.test(cleaned) || /^-?\d+\.\d{0,2}$/.test(cleaned)) {
    return cleaned; // Return as string to preserve decimal during input
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? '' : parsed;
};

const LeadForm = ({ lead, mode, onSubmit, onCancel, hiddenFields: propHiddenFields }) => {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantCode =
    user?.selectedTenant?.code ||
    user?.selectedTenant?.tenant_code ||
    user?.tenantCode ||
    null;
  
  // Load entity configuration with proper caching and loading states
  const { config: entityConfig, loading: configLoading, error: configError } = useEntityConfig('lead');
  
  // State for dynamic list values
  const [priorityOptions, setPriorityOptions] = useState([
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Critical', label: 'Critical' },
  ]);
  const [statusOptions, setStatusOptions] = useState([
    { value: 'New', label: 'New' },
    { value: 'Contacted', label: 'Contacted' },
    { value: 'Qualified', label: 'Qualified' },
    { value: 'Unqualified', label: 'Unqualified' },
    { value: 'Converted', label: 'Converted' },
  ]);
  const [hiddenFields, setHiddenFields] = useState(propHiddenFields || []);
  const [customAttributeFields, setCustomAttributeFields] = useState([]);
  const [attributes, setAttributes] = useState(() =>
    parseAttributes(lead?.Attributes || lead?.attributes)
  );
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    Title: lead?.Title || '',
    LeadSource: lead?.LeadSource || '',
    Status: lead?.Status || 'New',
    Priority: lead?.Priority || 'Medium',
    
    // Contact Information
    ContactName: lead?.ContactName || '',
    ContactEmail: lead?.ContactEmail || '',
    ContactPhone: lead?.ContactPhone || '',
    JobTitle: lead?.JobTitle || '',
    
    // Company Information
    CompanyName: lead?.CompanyName || '',
    Industry: lead?.Industry || '',
    CompanySize: lead?.CompanySize || '',
    Website: lead?.Website || '',
    
    // Description and Notes
    Description: lead?.Description || '',
    Notes: lead?.Notes || '',
    
    // Scoring and Qualification
    Score: lead?.Score || '',
    QualificationLevel: lead?.QualificationLevel || '',
    
    // Financial Information
    EstimatedBudget: lead?.EstimatedBudget || '',
    Currency: lead?.Currency || 'THB',
    
    // Timeline
    ExpectedCloseDate: formatDateForInput(lead?.ExpectedCloseDate),
    LastContactDate: formatDateForInput(lead?.LastContactDate),
    NextFollowUpDate: formatDateForInput(lead?.NextFollowUpDate),
    
    // Conversion Status (for edit mode)
    ConvertedToOpportunity: lead?.ConvertedToOpportunity || false,
    ConvertedToCustomer: lead?.ConvertedToCustomer || false,
    
    // Salesperson
    SalespersonID: lead?.SalespersonID ? String(lead.SalespersonID) : (user?.tenantUserId || user?.userId || user?.UserID ? String(user.tenantUserId || user.userId || user.UserID) : ''),
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const [dataGovernancePolicy, setDataGovernancePolicy] = useState(null);
  const [showSalespersonWarning, setShowSalespersonWarning] = useState(false);
  const [originalSalespersonID, setOriginalSalespersonID] = useState(null);
  const [showOpportunitySyncDialog, setShowOpportunitySyncDialog] = useState(false);
  const [pendingSyncData, setPendingSyncData] = useState(null);
  const [syncingOpportunity, setSyncingOpportunity] = useState(false);

  // Fetch salespersons and data governance policy on component mount
  useEffect(() => {
    const fetchSalespersons = async () => {
      try {
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/users/salespersons', {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Filter to only include users with Salesperson role (exclude Admin-only users)
          const salespersonOnly = (data.salespersons || []).filter(user => {
            const roles = user.Roles || '';
            // Include if user has Salesperson role (may also have Admin, but must have Salesperson)
            return roles.includes('Salesperson');
          });
          setSalespersons(salespersonOnly);
        }
      } catch (error) {
        console.error('Error fetching salespersons:', error);
      }
    };
    
    const fetchDataGovernancePolicy = async () => {
      try {
        const response = await dataGovernanceService.getPolicy('Lead');
        // Handle different response structures
        const policy = response?.policy || response?.data || response;
        setDataGovernancePolicy(policy);
        console.log('🔍 LeadForm: Data governance policy loaded:', policy);
      } catch (error) {
        console.error('Error fetching data governance policy:', error);
      }
    };
    
    fetchSalespersons();
    fetchDataGovernancePolicy();
  }, []);

  // When opened via route /leads/:id/edit without a provided lead prop, fetch the lead
  useEffect(() => {
    const maybeFetchLead = async () => {
      if (mode === 'edit' && !lead && params?.id) {
        try {
          setInitializing(true);
          const data = await api.get(`/leads/${params.id}`);
          const ld = data.lead || data || {};
          setFormData(prev => ({
            ...prev,
            // Basic Information
            Title: ld.Title || '',
            LeadSource: ld.LeadSource || '',
            Status: ld.Status || 'New',
            Priority: ld.Priority || 'Medium',
            // Contact Information
            ContactName: ld.ContactName || '',
            ContactEmail: ld.ContactEmail || '',
            ContactPhone: ld.ContactPhone || '',
            JobTitle: ld.JobTitle || '',
            // Company Information
            CompanyName: ld.CompanyName || '',
            Industry: ld.Industry || '',
            CompanySize: ld.CompanySize || '',
            Website: ld.Website || '',
            // Description and Notes
            Description: ld.Description || '',
            Notes: ld.Notes || '',
            // Scoring and Qualification
            Score: ld.Score || '',
            QualificationLevel: ld.QualificationLevel || '',
            // Financial Information
            EstimatedBudget: ld.EstimatedBudget || '',
            Currency: ld.Currency || 'THB',
            // Timeline
            ExpectedCloseDate: formatDateForInput(ld.ExpectedCloseDate),
            LastContactDate: formatDateForInput(ld.LastContactDate),
            NextFollowUpDate: formatDateForInput(ld.NextFollowUpDate),
            // Conversion Status
            ConvertedToOpportunity: ld.ConvertedToOpportunity || false,
            ConvertedToCustomer: ld.ConvertedToCustomer || false,
            // Salesperson
            SalespersonID: ld.SalespersonID ? String(ld.SalespersonID) : (user?.tenantUserId || user?.userId || user?.UserID ? String(user.tenantUserId || user.userId || user.UserID) : ''),
          }));
          setAttributes(parseAttributes(ld.Attributes || ld.attributes));
          setOriginalSalespersonID(ld.SalespersonID ? String(ld.SalespersonID) : null);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('LeadForm: failed to load lead for edit route', e);
        } finally {
          setInitializing(false);
        }
      }
    };
    maybeFetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, params?.id]);

  // Set original salesperson ID when lead prop changes
  useEffect(() => {
    if (lead?.SalespersonID) {
      setOriginalSalespersonID(String(lead.SalespersonID));
    }
  }, [lead?.SalespersonID]);

  // Load linked customer and auto-fill form data when in edit mode
  useEffect(() => {
    const loadLinkedCustomer = async () => {
      if (mode !== 'edit') return;
      
      const leadId = lead?.LeadID || params?.id;
      if (!leadId) return;

      try {
        // Load related customers
        const custRelResp = await entityRelationService.getRelatedEntitiesByType('Lead', Number(leadId), 'Customer', { timeoutMs: 12000 });
        const customerRelations = custRelResp?.relations || (Array.isArray(custRelResp) ? custRelResp : []);
        const customerIds = customerRelations.map(r => r.TargetEntityID || r.targetEntityID).filter(Boolean);

        if (customerIds.length > 0) {
          // Use the first customer as the primary linked customer
          const customerId = customerIds[0];
          try {
            const customerDetail = await api.get(`/customers/${customerId}`);
            const customer = customerDetail?.customer || customerDetail || {};
            
            console.log('🔍 LeadForm: Linked customer found, auto-filling form:', customer);

            // Auto-fill form data from customer (similar to LeadDetail.js logic)
            setFormData(prev => {
              const updates = { ...prev };
              
              // Contact Information - only fill if lead fields are empty
              if (customer.CustomerType === 'Person') {
                if (!prev.ContactName || prev.ContactName.trim() === '') {
                  updates.ContactName = `${customer.FirstName || ''} ${customer.LastName || ''}`.trim() || customer.CustomerName || '';
                }
                if (!prev.JobTitle || prev.JobTitle.trim() === '') {
                  updates.JobTitle = customer.JobTitle || customer.Position || '';
                }
                if (!prev.ContactEmail || prev.ContactEmail.trim() === '') {
                  updates.ContactEmail = customer.PersonEmail || customer.Email || '';
                }
                if (!prev.ContactPhone || prev.ContactPhone.trim() === '') {
                  updates.ContactPhone = customer.PersonPhone || customer.Phone || '';
                }
                // Company Information for Person customer
                if (!prev.CompanyName || prev.CompanyName.trim() === '') {
                  updates.CompanyName = customer.PersonCompanyName || customer.CompanyName || '';
                }
                if (!prev.Industry || prev.Industry.trim() === '') {
                  updates.Industry = customer.PersonCompanyIndustry || customer.Industry || '';
                }
                if (!prev.Website || prev.Website.trim() === '') {
                  updates.Website = customer.PersonCompanyWebsite || customer.Website || '';
                }
              } else {
                // Company customer
                if (!prev.CompanyName || prev.CompanyName.trim() === '') {
                  updates.CompanyName = customer.CompanyName || customer.CustomerName || '';
                }
                if (!prev.Industry || prev.Industry.trim() === '') {
                  updates.Industry = customer.Industry || '';
                }
                if (!prev.Website || prev.Website.trim() === '') {
                  updates.Website = customer.Website || '';
                }
                // Contact person for company
                if (!prev.ContactName || prev.ContactName.trim() === '') {
                  updates.ContactName = `${customer.FirstName || ''} ${customer.LastName || ''}`.trim() || customer.CustomerName || '';
                }
                if (!prev.JobTitle || prev.JobTitle.trim() === '') {
                  updates.JobTitle = customer.JobTitle || customer.Position || '';
                }
                if (!prev.ContactEmail || prev.ContactEmail.trim() === '') {
                  updates.ContactEmail = customer.PersonEmail || customer.Email || '';
                }
                if (!prev.ContactPhone || prev.ContactPhone.trim() === '') {
                  updates.ContactPhone = customer.PersonPhone || customer.Phone || '';
                }
              }
              
              return updates;
            });
          } catch (e) {
            console.warn('LeadForm: Failed to load customer detail for auto-fill:', e);
          }
        }
      } catch (e) {
        console.warn('LeadForm: Failed to load linked customer:', e);
      }
    };

    loadLinkedCustomer();
  }, [mode, lead?.LeadID, params?.id]);

  useEffect(() => {
    if (lead) {
      setAttributes(parseAttributes(lead.Attributes || lead.attributes));
    } else if (mode === 'create') {
      setAttributes({});
    }
  }, [lead?.Attributes, lead?.attributes, mode]);

  useEffect(() => {
    if (!entityConfig) return;
    const extractedFields = extractCustomAttributeFields(entityConfig, {
      tenantCode,
      includeGlobal: true
    });
    setCustomAttributeFields(extractedFields);
  }, [entityConfig, tenantCode]);

  useEffect(() => {
    if (customAttributeFields.length === 0) return;
    setAttributes((prev) => {
      const next = { ...prev };
      let changed = false;
      customAttributeFields.forEach((field) => {
        if (!(field.name in next)) {
          next[field.name] = '';
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [customAttributeFields]);

  // Related Entities state (Customer, Opportunity, Activity, SaleNote)
  const [relatedEntities, setRelatedEntities] = useState([]); // {type, id, name}[]
  const [showHelpModal, setShowHelpModal] = useState(false);
  const initialRelationKeysRef = useRef(new Set());
  const prefillAppliedRef = useRef(false);
  const [entitySelectionModal, setEntitySelectionModal] = useState({
    isOpen: false,
    entityType: null,
    searchTerm: '',
    results: [],
    loading: false,
    selectedIds: []
  });
  const [searchTimeouts, setSearchTimeouts] = useState({});
  const entityTypes = [
    { value: 'Customer', label: t('activities.entityTypes.customer', 'Customer') },
    { value: 'Opportunity', label: t('activities.entityTypes.opportunity', 'Opportunity') },
    { value: 'Activity', label: t('activities.activity', 'Activity') },
    { value: 'SaleNote', label: t('activities.entityTypes.saleNote', 'Sale Note') }
  ];

  const validate = () => {
    const errs = {};
    
    // At least one of ContactName or CompanyName is required
    if (isEmpty(formData.ContactName) && isEmpty(formData.CompanyName)) {
      errs.contact = 'กรุณากรอกชื่อผู้ติดต่อ หรือ ชื่อบริษัท / Please enter Contact Name or Company Name';
    }
    
    // Email format validation
    if (!isEmpty(formData.ContactEmail) && !isValidEmail(formData.ContactEmail)) {
      errs.ContactEmail = 'รูปแบบอีเมลไม่ถูกต้อง / Invalid email format';
    }
    
    // Website format validation
    if (!isEmpty(formData.Website) && !isValidUrl(formData.Website)) {
      errs.Website = 'รูปแบบ URL ไม่ถูกต้อง / Invalid URL format';
    }
    
    // Score validation (0-100)
    if (!isEmpty(formData.Score) && !isInRange(formData.Score, 0, 100)) {
      errs.Score = 'คะแนนต้องอยู่ระหว่าง 0-100 / Score must be between 0-100';
    }
    
    // Budget validation (positive)
    if (!isEmpty(formData.EstimatedBudget) && !isPositiveNumber(formData.EstimatedBudget)) {
      errs.EstimatedBudget = 'งบประมาณต้องเป็นตัวเลขบวก / Budget must be a positive number';
    }
    
    // Date validations
    if (!isEmpty(formData.ExpectedCloseDate) && !isEmpty(formData.LastContactDate)) {
      if (new Date(formData.ExpectedCloseDate) < new Date(formData.LastContactDate)) {
        errs.ExpectedCloseDate = 'วันปิดดีลต้องอยู่หลังวันติดต่อล่าสุด / Close date must be after last contact date';
      }
    }
    
    setErrors(errs);
    
    // Scroll to first error
    if (Object.keys(errs).length > 0) {
      scrollToFirstError(errs);
    }
    
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    try {
      // Clean up the data before sending
      const submitData = { ...formData };
      submitData.ContactEmail = normalizeEmailList(submitData.ContactEmail);
      const sanitizedAttributes = sanitizeAttributePayload(
        attributes,
        customAttributeFields
      );
      submitData.attributes = sanitizedAttributes;
      submitData.Attributes = sanitizedAttributes;
      
      // Convert empty strings to null for optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });
      
      // Convert numeric fields
      if (submitData.Score) {
        submitData.Score = parseInt(submitData.Score);
      }
      if (submitData.EstimatedBudget) {
        submitData.EstimatedBudget = parseFloat(submitData.EstimatedBudget);
      }
      
      // Map SalespersonID to salespersonId for API
      if (submitData.SalespersonID) {
        submitData.salespersonId = submitData.SalespersonID;
      }
      
      // Handle submit - use provided onSubmit or fallback to direct API call
      let submitResult;
      if (typeof onSubmit === 'function') {
        submitResult = await onSubmit(submitData);
      } else {
        // Fallback: Direct API call when onSubmit is not provided (route-based usage)
        const leadId = lead?.LeadID || params?.id;
        const isEdit = mode === 'edit' && leadId;
        
        try {
          if (isEdit) {
            submitResult = await api.put(`/leads/${leadId}`, submitData);
            toast.success(t('leads.updateSuccess', 'Lead updated successfully'));
          } else {
            submitResult = await api.post('/leads', submitData);
            toast.success(t('leads.createSuccess', 'Lead created successfully'));
          }
        } catch (apiError) {
          console.error('LeadForm: API error:', apiError);
          toast.error(apiError.message || t('leads.saveError', 'Failed to save lead'));
          throw apiError;
        }
      }

      // After save, sync relationships if LeadID is available
      let leadId = lead?.LeadID;
      if (!leadId && submitResult) {
        leadId = submitResult?.lead?.LeadID || submitResult?.LeadID || submitResult?.id || submitResult?.leadId;
      }

      if (leadId) {
        try {
          // Build existing set from cache (loaded on edit)
          const existingKeys = new Set(initialRelationKeysRef.current ? Array.from(initialRelationKeysRef.current) : []);
          const selectedKeys = new Set(relatedEntities.map(e => `${e.type}:${e.id}`));

          const toCreate = relatedEntities.filter(e => !existingKeys.has(`${e.type}:${e.id}`));
          // Compute deletions only when we have initial cache (handled below)

          const createPromises = toCreate.map(e =>
            entityRelationService.create({
              sourceEntityType: 'Lead',
              sourceEntityID: Number(leadId),
              targetEntityType: e.type,
              targetEntityID: e.id,
              relationType: 'related_to'
            })
          );

          // We only know RelationID if we fetched relations; when we didn't, skip deletions
          let deletePromises = [];
          if (initialRelationKeysRef.current && initialRelationKeysRef.current.size > 0) {
            // Need actual RelationID list, refetch quickly
            try {
              console.log('🔍 LeadForm: Refetching existing relations for deletion comparison...');
              const existing = await entityRelationService.getRelatedEntities('Lead', Number(leadId), { direction: 'both' });
              console.log('🔍 LeadForm: Existing relations fetched:', existing);
              
              const relations = existing.relations || [];
              const relationsToDelete = relations.filter(rel => {
                // Handle different casing
                const srcType = rel.SourceEntityType || rel.sourceEntityType || '';
                const tgtType = rel.TargetEntityType || rel.targetEntityType || '';
                const srcId = rel.SourceEntityID || rel.sourceEntityID;
                const tgtId = rel.TargetEntityID || rel.targetEntityID;
                
                const isLeadSource = srcType.toLowerCase() === 'lead';
                const entityType = isLeadSource ? tgtType : srcType;
                const entityId = isLeadSource ? tgtId : srcId;
                const key = `${entityType}:${entityId}`;
                
                const shouldDelete = !selectedKeys.has(key);
                console.log(`🔍 LeadForm: Checking relation ${key}, shouldDelete:`, shouldDelete);
                return shouldDelete;
              });
              
              console.log('🔍 LeadForm: Relations to delete:', relationsToDelete.length, relationsToDelete.map(r => r.RelationID || r.relationID));
              
              // Use batch delete for better performance and error handling
              if (relationsToDelete.length > 0) {
                const relationIDsToDelete = relationsToDelete.map(rel => rel.RelationID || rel.relationID).filter(Boolean);
                if (relationIDsToDelete.length > 0) {
                  deletePromises = [
                    entityRelationService.deleteBatch(relationIDsToDelete, 5000).then(batchResult => {
                      console.log('🔍 LeadForm: Batch delete result:', batchResult);
                      // Return array of individual results for Promise.allSettled
                      return batchResult.results.map(r => {
                        if (r.success) {
                          return Promise.resolve(r);
                        } else {
                          return Promise.reject(new Error(r.error));
                        }
                      });
                    })
                  ];
                }
              }
            } catch (e) {
              // Non-blocking
              console.warn('LeadForm: Failed to refetch relations for deletions', e);
            }
          }

          console.log('🔍 LeadForm: Syncing relations - creating:', createPromises.length, 'deleting:', deletePromises.length);
          
          // Execute creates and batch deletes
          const createResults = await Promise.allSettled(createPromises);
          const deleteResults = deletePromises.length > 0 ? await Promise.allSettled(deletePromises) : [];
          
          console.log('🔍 LeadForm: Create results:', createResults);
          console.log('🔍 LeadForm: Delete results:', deleteResults);
          
          // Check for failures
          const createFailed = createResults.filter(r => r.status === 'rejected');
          let deleteFailed = [];
          
          // Handle batch delete results
          if (deleteResults.length > 0 && deleteResults[0].status === 'fulfilled') {
            const batchResult = deleteResults[0].value;
            deleteFailed = batchResult.results ? batchResult.results.filter(r => !r.success) : [];
          } else if (deleteResults.length > 0 && deleteResults[0].status === 'rejected') {
            deleteFailed = [{ error: deleteResults[0].reason?.message || 'Batch delete failed' }];
          }
          
          const totalFailed = createFailed.length + deleteFailed.length;
          
          if (totalFailed > 0) {
            const failedDetails = [
              ...createFailed.map(r => `  • Create failed: ${r.reason?.message || 'Unknown error'}`),
              ...deleteFailed.map(r => `  • Delete failed (ID ${r.relationID}): ${r.error || 'Unknown error'}`)
            ].join('\n');
            console.error('❌ LeadForm: Some relation operations failed:\n' + failedDetails);
            alert(`Lead saved successfully, but ${totalFailed} relationship operation(s) failed:\n\n${failedDetails}\n\nPlease try editing the lead again to update relationships.`);
          } else {
            console.log('✅ LeadForm: All relationship operations completed successfully');
          }
        } catch (relErr) {
          console.error('❌ LeadForm: Relationship sync failed with exception:', relErr);
          alert(`Lead saved successfully, but relationships could not be updated:\n\n${relErr.message || 'Unknown error'}\n\nPlease try editing the lead again to update relationships.`);
        }
      } else {
        console.warn('LeadForm: LeadID not available after save; skipping relationship sync');
      }
      
      // Check if this is a converted lead and offer to sync with opportunity
      const isConvertedLead = mode === 'edit' && (formData.ConvertedToOpportunity || lead?.ConvertedToOpportunity);
      
      if (isConvertedLead && leadId) {
        // Try to find related opportunity
        try {
          const relatedOpp = await entityRelationService.getLeadOpportunity(Number(leadId));
          if (relatedOpp?.opportunityId) {
            // Store sync data and show dialog
            setPendingSyncData({
              opportunityId: relatedOpp.opportunityId,
              leadId: leadId,
              submitData: submitData,
              savedLeadId: leadId
            });
            setShowOpportunitySyncDialog(true);
            // Don't navigate yet - wait for user decision
            return;
          }
        } catch (syncCheckErr) {
          console.warn('LeadForm: Could not check for related opportunity:', syncCheckErr);
        }
      }
      
      // Navigate after successful save when onSubmit was not provided (route-based usage)
      if (typeof onSubmit !== 'function') {
        const savedLeadId = leadId || submitResult?.lead?.LeadID || submitResult?.LeadID || submitResult?.id;
        if (savedLeadId) {
          navigate(`/leads/${savedLeadId}`, { state: { forceRefresh: Date.now() } });
        } else {
          navigate('/leads');
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle syncing lead changes to related opportunity
  const handleSyncOpportunity = async () => {
    if (!pendingSyncData) return;
    
    setSyncingOpportunity(true);
    try {
      const { opportunityId, submitData, savedLeadId } = pendingSyncData;
      
      // Map lead fields to opportunity fields
      const opportunityUpdateData = {
        ContactName: submitData.ContactName || null,
        ContactEmail: submitData.ContactEmail || null,
        ContactPhone: submitData.ContactPhone || null,
        CustomerName: submitData.CompanyName || submitData.ContactName || null,
        Industry: submitData.Industry || null,
        // Map EstimatedBudget to Value
        Value: submitData.EstimatedBudget ? parseFloat(submitData.EstimatedBudget) : undefined,
        Currency: submitData.Currency || undefined,
        // Copy attributes
        Attributes: submitData.attributes || submitData.Attributes || undefined
      };
      
      // Remove undefined values
      Object.keys(opportunityUpdateData).forEach(key => {
        if (opportunityUpdateData[key] === undefined) {
          delete opportunityUpdateData[key];
        }
      });
      
      // Call opportunity update API
      await api.put(`/opportunities/${opportunityId}`, opportunityUpdateData);
      toast.success(t('leads.opportunitySyncSuccess', 'ซิงค์ข้อมูลกับโอกาสทางการขายเรียบร้อยแล้ว / Opportunity updated successfully'));
      
      setShowOpportunitySyncDialog(false);
      setPendingSyncData(null);
      
      // Navigate to lead detail
      if (typeof onSubmit !== 'function' && savedLeadId) {
        navigate(`/leads/${savedLeadId}`, { state: { forceRefresh: Date.now() } });
      }
    } catch (syncErr) {
      console.error('LeadForm: Failed to sync with opportunity:', syncErr);
      toast.error(t('leads.opportunitySyncError', 'ไม่สามารถซิงค์ข้อมูลกับโอกาสได้ / Failed to sync with opportunity'));
    } finally {
      setSyncingOpportunity(false);
    }
  };

  // Skip syncing and just navigate
  const handleSkipSync = () => {
    const savedLeadId = pendingSyncData?.savedLeadId;
    setShowOpportunitySyncDialog(false);
    setPendingSyncData(null);
    
    // Navigate to lead detail
    if (typeof onSubmit !== 'function' && savedLeadId) {
      navigate(`/leads/${savedLeadId}`, { state: { forceRefresh: Date.now() } });
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Check for salesperson change warning
    if (field === 'SalespersonID' && mode === 'edit') {
      const newSalespersonID = String(value || '');
      
      // Set original salesperson ID if not set yet
      if (!originalSalespersonID && newSalespersonID) {
        setOriginalSalespersonID(newSalespersonID);
        return; // Don't show warning on initial load
      }
      
      if (originalSalespersonID) {
        const hasChanged = newSalespersonID !== originalSalespersonID && newSalespersonID !== '';
        const currentUserId = String(user?.tenantUserId || user?.userId || user?.UserID || '');
        
        // Show warning if policy is 1 or 2 and user is changing salesperson to someone else
        if (hasChanged && dataGovernancePolicy) {
          const policyType = Number(dataGovernancePolicy.policyType || dataGovernancePolicy.PolicyType || 0);
          const isPolicy1Or2 = policyType === 1 || policyType === 2;
          const isChangingToOther = newSalespersonID !== currentUserId;
          
          console.log('🔍 LeadForm: Salesperson change check:', {
            originalSalespersonID,
            newSalespersonID,
            currentUserId,
            policyType,
            isPolicy1Or2,
            isChangingToOther,
            dataGovernancePolicy
          });
          
          if (isPolicy1Or2 && isChangingToOther) {
            setShowSalespersonWarning(true);
          } else {
            setShowSalespersonWarning(false);
          }
        } else {
          setShowSalespersonWarning(false);
        }
      }
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAddCustomAttributeField = async ({
    fieldName,
    label,
    type,
    options: choiceOptions = [],
    columns: tableColumns = []
  }) => {
    if (!tenantCode) {
      const errorMessage = 'Tenant context is required to add custom fields.';
      console.error(`❌ LeadForm: ${errorMessage}`);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    const orderBase = 900 + customAttributeFields.length * 5;
    const isChoiceField = type === 'choice';
    const isTableField = type === 'table';
    const dataType =
      type === 'number' ? 'decimal' : type === 'date' ? 'date' : 'string';
    const timestamp = new Date().toISOString();

    if (isChoiceField && choiceOptions.length === 0) {
      const message = 'At least one option is required for choice fields.';
      console.error(`❌ LeadForm: ${message}`);
      toast.error(message);
      throw new Error(message);
    }

    if (isTableField && tableColumns.length === 0) {
      const message = 'At least one column is required for table fields.';
      console.error(`❌ LeadForm: ${message}`);
      toast.error(message);
      throw new Error(message);
    }

    const slugifyOptionValue = (inputValue = '', fallbackIndex = 0) => {
      const normalised = inputValue
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '');

      if (normalised) return normalised;
      return `option_${fallbackIndex + 1}`;
    };

    const seenOptionValues = new Set();
    const normalizedOptions = isChoiceField
      ? choiceOptions.map((option, index) => {
          let baseValue = slugifyOptionValue(
            option?.value ?? option?.label ?? '',
            index
          );
          let uniqueValue = baseValue;
          let counter = 1;

          while (seenOptionValues.has(uniqueValue)) {
            counter += 1;
            uniqueValue = slugifyOptionValue(
              `${baseValue}_${counter}`,
              index + counter
            );
          }

          seenOptionValues.add(uniqueValue);

          return {
            value: uniqueValue,
            label: option?.label ?? uniqueValue,
            ...(option?.labelTH ? { labelTH: option.labelTH } : {})
          };
        })
      : [];

    const fieldConfig = {
      displayName: label,
      displayNameTH: label,
      description: `Custom attribute "${label}"`,
      dataType,
      metadata: {
        isCustomAttribute: true,
        attributeType: isChoiceField ? 'choice' : type,
        attributeSource: 'attributes',
        createdAt: timestamp,
        tenantCode,
        tenantOnly: true
      },
      storage: {
        type: 'attributes',
        path: fieldName
      },
      validation: {
        required: false,
        ...(isChoiceField
          ? { enum: normalizedOptions.map((option) => option.value) }
          : {})
      },
      ui: {
        inputType: isChoiceField ? 'select' : type,
        showInForm: true,
        showInDetail: true,
        showInList: false,
        showInExport: false,
        group: 'Lead Additional Data',
        order: orderBase,
        ...(isChoiceField ? { options: normalizedOptions } : {}),
        ...(isTableField ? { columns: tableColumns } : {})
      },
      ...(isTableField ? { columns: tableColumns } : {}),
      _source: 'tenant_custom',
      _source_tenant: tenantCode,
      _created_at: timestamp,
      _tenantSpecific: true,
      _tenantCode: tenantCode
    };

    const response = await fetch('/api/tenant-entity-config/lead/custom-field', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      },
      body: JSON.stringify({
        fieldName,
        fieldConfig
      })
    });

    let responseBody = null;
    if (!response.ok) {
      responseBody = await response.json().catch(() => ({}));
      console.error('❌ LeadForm: Failed to add custom field:', responseBody);
      const message = responseBody?.message || 'Failed to add custom field';
      toast.error(message);
      throw new Error(message);
    } else {
      responseBody = await response.json().catch(() => ({}));
    }

    clearConfigCache('lead');
    const updatedConfig = await loadEntityConfig('lead', tenantCode);
    const updatedFields = extractCustomAttributeFields(
      updatedConfig || entityConfig || {},
      {
        tenantCode,
        includeGlobal: true
      }
    );
    setCustomAttributeFields(updatedFields);
    setAttributes((prev) => {
      const next = { ...prev };
      updatedFields.forEach((field) => {
        if (!(field.name in next)) {
          next[field.name] = '';
        }
      });
      return next;
    });
    toast.success(t('customAttributes.fieldAdded', 'Custom field added'));
    return responseBody;
  };

  const handleCustomerSelect = (contactInfo) => {
    // Get the customer object for direct field access
    const customer = contactInfo?.customer || contactInfo?.selectedCustomer || contactInfo;
    
    // Map customer data to lead contact fields properly
    // Use JobTitle field, NOT PersonTitle (which is name prefix like "นาย")
    const mappedContactInfo = {
      // Contact Information - prioritize mapped fields
      ContactName: contactInfo.ContactName || '',
      ContactEmail: contactInfo.ContactEmail || '',
      ContactPhone: contactInfo.ContactPhone || '',
      JobTitle: contactInfo.JobTitle || customer?.JobTitle || '',
      // Company Information - get directly from customer object to ensure proper mapping
      CompanyName: customer?.CustomerType === 'Company' 
        ? (customer?.CompanyName || contactInfo.CompanyName || '')
        : (customer?.PersonCompanyName || contactInfo.CompanyName || ''),
      Industry: customer?.Industry || customer?.PersonCompanyIndustry || contactInfo.Industry || '',
      Website: customer?.Website || contactInfo.Website || ''
    };

    console.log('🔍 LeadForm: Customer selected, mapping contact info:', {
      original: contactInfo,
      customer: customer,
      mapped: mappedContactInfo
    });

    // Update form data with mapped contact information
    setFormData(prev => ({
      ...prev,
      ...mappedContactInfo
    }));

    // Try to auto-add Customer (and key relations) as related entities
    try {
      const selectedCustomer = contactInfo?.customer || contactInfo?.selectedCustomer || null;
      let customerId = contactInfo?.CustomerID || contactInfo?.customerId || contactInfo?.id || selectedCustomer?.CustomerID || selectedCustomer?.id;
      if (customerId) {
        // Build customer display name
        const customerName = (() => {
          const src = selectedCustomer || contactInfo;
          const personName = (src.CustomerName && src.CustomerName.trim())
            || `${(src.FirstName || '').trim()} ${(src.LastName || '').trim()}`.trim();
          return personName || src.CompanyName || `Customer ${customerId}`;
        })();

        // Add/merge Customer entity
        setRelatedEntities(prev => {
          const exists = prev.some(e => e.type === 'Customer' && String(e.id) === String(customerId));
          const merged = exists ? prev : [...prev, { type: 'Customer', id: Number(customerId), name: customerName }];
          return merged;
        });

        // Fetch related Opportunities and Activities, then merge
        (async () => {
          try {
            const relResp = await entityRelationService.getRelatedEntities('Customer', Number(customerId), { direction: 'both', timeoutMs: 12000 });
            const relations = Array.isArray(relResp) ? relResp : (relResp?.relations || []);

            const toAdd = [];
            relations.forEach(rel => {
              const srcType = rel.SourceEntityType || rel.sourceEntityType || '';
              const tgtType = rel.TargetEntityType || rel.targetEntityType || '';
              const srcId = rel.SourceEntityID || rel.sourceEntityID;
              const tgtId = rel.TargetEntityID || rel.targetEntityID;
              const isCustomerSource = (srcType || '').toString().toLowerCase() === 'customer';
              const relatedTypeRaw = isCustomerSource ? tgtType : srcType;
              const relatedType = (relatedTypeRaw || '').toString();
              const relatedId = isCustomerSource ? tgtId : srcId;

              if (!relatedId) return;
              if (relatedType === 'Opportunity') {
                const name = rel.OpportunityName || rel.Title || `Opportunity ${relatedId}`;
                toAdd.push({ type: 'Opportunity', id: Number(relatedId), name });
              } else if (relatedType === 'Activity') {
                const name = rel.Subject || `${rel.ActivityType || 'Activity'} ${relatedId}`;
                toAdd.push({ type: 'Activity', id: Number(relatedId), name });
              }
            });

            if (toAdd.length > 0) {
              setRelatedEntities(prev => {
                const existingKeys = new Set(prev.map(e => `${e.type}:${e.id}`));
                const deduped = toAdd.filter(e => !existingKeys.has(`${e.type}:${e.id}`));
                return deduped.length > 0 ? [...prev, ...deduped] : prev;
              });
            }
          } catch (e) {
            // Non-blocking enrichment failure
            console.warn('LeadForm: Failed to load customer relations for auto-linking', e);
          }
        })();
      } else {
        // Try to resolve customer by searching when ID is missing
        const searchTerm = contactInfo?.CompanyName 
          || contactInfo?.CustomerName 
          || `${(contactInfo?.FirstName || '').trim()} ${(contactInfo?.LastName || '').trim()}`.trim()
          || contactInfo?.Email 
          || contactInfo?.Phone 
          || '';
        if (searchTerm && searchTerm.length >= 2) {
          (async () => {
            try {
              const resp = await fetch(`/api/customers/search/${encodeURIComponent(searchTerm)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
              if (resp.ok) {
                const data = await resp.json();
                const result = (data.results || [])[0];
                if (result && result.CustomerID) {
                  const name = result.CustomerType === 'Person'
                    ? `${result.FirstName || ''} ${result.LastName || ''}`.trim() || result.CustomerName
                    : result.CompanyName || result.CustomerName || `Customer ${result.CustomerID}`;
                  setRelatedEntities(prev => {
                    const exists = prev.some(e => e.type === 'Customer' && String(e.id) === String(result.CustomerID));
                    return exists ? prev : [...prev, { type: 'Customer', id: Number(result.CustomerID), name }];
                  });
                }
              }
            } catch (e) {
              // ignore lookup failures
            }
          })();
        }
      }
    } catch (e) {
      console.warn('LeadForm: Auto-add related entities failed', e);
    }
  };

  // Helper: get entity id from API payload
  const getEntityId = (entity, entityType) => {
    switch (entityType) {
      case 'Customer': return entity.CustomerID;
      case 'Opportunity': return entity.OpportunityID;
      case 'Activity': return entity.ActivityID;
      case 'SaleNote': return entity.SaleNoteID || entity.NoteID;
      default: return null;
    }
  };

  // Helper: get display name
  const getEntityName = (entity, entityType) => {
    switch (entityType) {
      case 'Customer': {
        const personName = (entity.CustomerName && entity.CustomerName.trim())
          || `${(entity.FirstName || '').trim()} ${(entity.LastName || '').trim()}`.trim();
        return personName || entity.CompanyName || `Customer ${entity.CustomerID}`;
      }
      case 'Opportunity': return entity.OpportunityName || entity.Title || `Opportunity ${entity.OpportunityID}`;
      case 'Activity': return entity.Subject || `${entity.ActivityType || 'Activity'} ${entity.ActivityID}`;
      case 'SaleNote': {
        const noteText = entity.NoteText || '';
        const firstLine = noteText.split('\n')[0] || noteText;
        const previewText = firstLine.length > 100 ? firstLine.substring(0, 100).trim() + '...' : firstLine.trim();
        return previewText || `Sale Note ${entity.NoteID || entity.SaleNoteID}`;
      }
      default: return `${entityType}`;
    }
  };

  const getEntitySubInfo = (entity, entityType) => {
    switch (entityType) {
      case 'Customer':
        return entity.CompanyName || entity.Email || entity.Phone || '';
      case 'Opportunity':
        return entity.Stage || '';
      case 'Activity':
        return entity.ActivityType || entity.Status || '';
      case 'SaleNote':
        if (entity.NoteDate) {
          try {
            return new Date(entity.NoteDate).toLocaleDateString();
          } catch (_) {
            return '';
          }
        }
        return '';
      default:
        return '';
    }
  };

  // Load hidden fields from entity config
  useEffect(() => {
    // Check for undefined/null/empty array
    if (entityConfig && (propHiddenFields === undefined || propHiddenFields === null || (Array.isArray(propHiddenFields) && propHiddenFields.length === 0))) {
      // Extract hidden fields from config
      // The entityConfig returned from useEntityConfig hook already has hiddenFields at root level
      const configHiddenFields = entityConfig.hiddenFields || [];
      if (Array.isArray(configHiddenFields)) {
        setHiddenFields(configHiddenFields);
        console.log('✅ LeadForm: Loaded hidden fields from config:', configHiddenFields);
      } else {
        console.warn('⚠️ LeadForm: hiddenFields is not an array:', configHiddenFields);
        setHiddenFields([]);
      }
    } else if (propHiddenFields) {
      setHiddenFields(propHiddenFields);
      console.log('✅ LeadForm: Using prop hiddenFields:', propHiddenFields);
    }
  }, [entityConfig, propHiddenFields]);

  // Load selection values from tenant config with global fallback
  useEffect(() => {
    if (!entityConfig) return; // Wait for entity config to load
    
    const loadConfig = async () => {
      
      try {
        console.log('🔍 LeadForm: Loading selection values from tenant config...');
        const response = await fetch('/api/tenant-selection/lead', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const selectionValues = data.selectionValues;
          
          console.log('🔍 LeadForm: Received selection values:', selectionValues);
          
          // Update Priority options
          if (selectionValues?.Priority?.values) {
            const priorityOpts = selectionValues.Priority.values.map(v => ({
              value: v.value,
              label: v.label
            }));
            setPriorityOptions(priorityOpts);
            console.log('✅ LeadForm: Set Priority options:', priorityOpts);
          }
          
          // Update Status options
          if (selectionValues?.Status?.values) {
            const statusOpts = selectionValues.Status.values.map(v => ({
              value: v.value,
              label: v.label
            }));
            setStatusOptions(statusOpts);
            console.log('✅ LeadForm: Set Status options:', statusOpts);
          }
          
          // Update QualificationLevel options
          if (selectionValues?.QualificationLevel?.values) {
            const qualOpts = selectionValues.QualificationLevel.values.map(v => ({
              value: v.value,
              label: v.label
            }));
            setQualificationOptions(qualOpts);
            console.log('✅ LeadForm: Set QualificationLevel options:', qualOpts);
          }
          
          // Update Currency options
          if (selectionValues?.Currency?.values) {
            const currencyOpts = selectionValues.Currency.values.map(v => ({
              value: v.value,
              label: v.label
            }));
            setCurrencyOptions(currencyOpts);
            console.log('✅ LeadForm: Set Currency options:', currencyOpts);
          }
        } else {
          console.error('❌ LeadForm: Failed to fetch selection values, status:', response.status);
        }
      } catch (error) {
        console.error('❌ LeadForm: Failed to load selection values:', error);
        // Keep using default options if loading fails
      }
    };
    
    loadConfig();
  }, [entityConfig]);

  // Load existing relations for Lead (edit mode)
  useEffect(() => {
    const loadRelations = async () => {
      if (!(mode === 'edit' && lead && lead.LeadID)) return;
      try {
        const relations = await entityRelationService.getRelatedEntities('Lead', lead.LeadID, { direction: 'both', timeoutMs: 12000 });
        const mapped = (relations.relations || []).map(rel => {
          const srcType = rel.SourceEntityType || rel.sourceEntityType;
          const tgtType = rel.TargetEntityType || rel.targetEntityType;
          const srcId = rel.SourceEntityID || rel.sourceEntityID;
          const tgtId = rel.TargetEntityID || rel.targetEntityID;
          const isLeadSource = srcType === 'Lead';
          const type = isLeadSource ? tgtType : srcType;
          const id = isLeadSource ? tgtId : srcId;
          return { type, id, name: '' };
        });

        // Cache initial keys
        initialRelationKeysRef.current = new Set(mapped.map(e => `${e.type}:${e.id}`));

        // Enrich names for display
        const enriched = await Promise.all(mapped.map(async (e) => {
          try {
            let endpoint = '';
            if (e.type === 'Customer') endpoint = `/customers/${e.id}`;
            else if (e.type === 'Opportunity') endpoint = `/opportunities/${e.id}`;
            else if (e.type === 'Activity') endpoint = `/activities/${e.id}`;
            else if (e.type === 'SaleNote') endpoint = `/sales-notes/${e.id}`;
            if (!endpoint) return { ...e, name: `${e.type} ${e.id}` };

            const data = await api.get(endpoint);
            const entity = data.customer || data.opportunity || data.activity || data.saleNote || data.salesNote || data;
            return { ...e, name: getEntityName(entity, e.type) };
          } catch {
            return { ...e, name: `${e.type} ${e.id}` };
          }
        }));

        setRelatedEntities(enriched);
      } catch (err) {
        console.warn('LeadForm: Failed to load existing relations', err);
      }
    };
    loadRelations();
  }, [mode, lead]);

  useEffect(() => {
    if (prefillAppliedRef.current || mode !== 'create') return;

    const search = location?.search;
    if (!search) {
      prefillAppliedRef.current = true;
      return;
    }

    const paramsFromQuery = new URLSearchParams(search);
    if (Array.from(paramsFromQuery.keys()).length === 0) {
      prefillAppliedRef.current = true;
      return;
    }

    const updates = {};
    const notesSections = [];
    let hasFormUpdates = false;

    const titleParam = paramsFromQuery.get('title');
    if (titleParam) {
      updates.Title = titleParam;
      hasFormUpdates = true;
    }

    const descriptionParam = paramsFromQuery.get('description');
    if (descriptionParam) {
      updates.Description = descriptionParam;
      hasFormUpdates = true;
    }

    const priorityParam = paramsFromQuery.get('priority');
    if (priorityParam) {
      const normalizedPriority = priorityParam.trim().toLowerCase();
      const capitalizedPriority =
        normalizedPriority.charAt(0).toUpperCase() + normalizedPriority.slice(1);
      const matchedPriority =
        priorityOptions.find(opt => opt.value.toLowerCase() === normalizedPriority) ||
        priorityOptions.find(opt => opt.value === capitalizedPriority);

      if (matchedPriority) {
        updates.Priority = matchedPriority.value;
        hasFormUpdates = true;
      } else if (normalizedPriority) {
        updates.Priority = capitalizedPriority;
        hasFormUpdates = true;
      }
    }

    const leadSourceParam = paramsFromQuery.get('leadSource');
    if (leadSourceParam) {
      updates.LeadSource = leadSourceParam;
      hasFormUpdates = true;
    } else if (titleParam) {
      updates.LeadSource = 'Lead Scout';
      hasFormUpdates = true;
    }

    const reasoningParam = paramsFromQuery.get('reasoning');
    if (reasoningParam) {
      notesSections.push(`Reasoning:\n${reasoningParam}`);
    }

    const estimatedValueParam = paramsFromQuery.get('estimatedValue');
    if (estimatedValueParam) {
      notesSections.push(`Estimated Value: ${estimatedValueParam}`);
    }

    const sourceUrlParam = paramsFromQuery.get('sourceUrl');
    if (sourceUrlParam) {
      notesSections.push(`Source: ${sourceUrlParam}`);
    }

    const actionsParam = paramsFromQuery.get('actions');
    if (actionsParam) {
      const parsedActions = actionsParam
        .split('||')
        .map(action => action.trim())
        .filter(Boolean);
      if (parsedActions.length > 0) {
        notesSections.push(
          `Suggested Actions:\n${parsedActions.map(action => `• ${action}`).join('\n')}`
        );
      }
    }

    const contactNameParam = paramsFromQuery.get('contactName');
    if (contactNameParam) {
      updates.ContactName = contactNameParam;
      hasFormUpdates = true;
    }

    const contactEmailParam = paramsFromQuery.get('contactEmail');
    if (contactEmailParam) {
      updates.ContactEmail = contactEmailParam;
      hasFormUpdates = true;
    }

    const contactPhoneParam = paramsFromQuery.get('contactPhone');
    if (contactPhoneParam) {
      updates.ContactPhone = contactPhoneParam;
      hasFormUpdates = true;
    }

    const jobTitleParam = paramsFromQuery.get('jobTitle');
    if (jobTitleParam) {
      updates.JobTitle = jobTitleParam;
      hasFormUpdates = true;
    }

    const companyNameParam = paramsFromQuery.get('companyName');
    if (companyNameParam) {
      updates.CompanyName = companyNameParam;
      hasFormUpdates = true;
    }

    const industryParam = paramsFromQuery.get('industry');
    if (industryParam) {
      updates.Industry = industryParam;
      hasFormUpdates = true;
    }

    const websiteParam = paramsFromQuery.get('website');
    if (websiteParam) {
      updates.Website = websiteParam;
      hasFormUpdates = true;
    }

    const notesContent = notesSections.length > 0 ? notesSections.join('\n\n') : '';

    if (hasFormUpdates || notesContent) {
      setFormData(prev => {
        const next = { ...prev, ...updates };
        if (notesContent) {
          const existingNotes = (prev.Notes || '').trim();
          next.Notes = existingNotes ? `${existingNotes}\n\n${notesContent}` : notesContent;
        }
        return next;
      });
    }

    const relatedType = paramsFromQuery.get('relatedEntityType');
    const relatedIdParam = paramsFromQuery.get('relatedEntityId');
    if (relatedType && relatedIdParam) {
      const relatedName = paramsFromQuery.get('entityName');
      setRelatedEntities(prev => {
        const exists = prev.some(
          entry => entry.type === relatedType && String(entry.id) === String(relatedIdParam)
        );
        if (exists) {
          return prev;
        }
        const numericId = Number(relatedIdParam);
        const idValue = Number.isNaN(numericId) ? relatedIdParam : numericId;
        return [
          ...prev,
          {
            type: relatedType,
            id: idValue,
            name: relatedName || `${relatedType} ${relatedIdParam}`
          }
        ];
      });
    }

    prefillAppliedRef.current = true;
  }, [location?.search, mode, priorityOptions]);

  // Open entity selection modal
  const openEntitySelectionModal = (entityType, currentSearchTerm = '') => {
    const alreadySelectedIds = relatedEntities
      .filter(e => e.type === entityType)
      .map(e => e.id.toString());

    setEntitySelectionModal({
      isOpen: true,
      entityType,
      searchTerm: currentSearchTerm,
      results: [],
      loading: true,
      selectedIds: alreadySelectedIds
    });
    performEntitySearchForModal(currentSearchTerm || '', entityType);
  };

  const closeEntitySelectionModal = (addSelected = false) => {
    if (addSelected && entitySelectionModal.entityType) {
      const newEntities = [];
      entitySelectionModal.results.forEach(entity => {
        const id = getEntityId(entity, entitySelectionModal.entityType);
        const idStr = id?.toString();
        if (!idStr) return;
        const isSelected = entitySelectionModal.selectedIds.includes(idStr);
        const already = relatedEntities.some(e => e.type === entitySelectionModal.entityType && e.id.toString() === idStr);
        if (isSelected && !already) {
          newEntities.push({
            type: entitySelectionModal.entityType,
            id,
            name: getEntityName(entity, entitySelectionModal.entityType)
          });
        }
      });
      // Remove deselected of this type
      const remainingIds = new Set(entitySelectionModal.selectedIds);
      const kept = relatedEntities.filter(e => e.type !== entitySelectionModal.entityType || remainingIds.has(e.id.toString()));
      setRelatedEntities([...kept, ...newEntities]);
    }

    setEntitySelectionModal({ isOpen: false, entityType: null, searchTerm: '', results: [], loading: false, selectedIds: [] });
  };

  const toggleEntitySelectionInModal = (entity, entityType) => {
    const idStr = getEntityId(entity, entityType)?.toString();
    if (!idStr) return;
    setEntitySelectionModal(prev => {
      const isSelected = prev.selectedIds.includes(idStr);
      return { ...prev, selectedIds: isSelected ? prev.selectedIds.filter(x => x !== idStr) : [...prev.selectedIds, idStr] };
    });
  };

  const handleEntityModalSearchChange = (value) => {
    setEntitySelectionModal(prev => ({ ...prev, searchTerm: value }));
    const entityType = entitySelectionModal.entityType;
    if (!entityType) return;
    const key = `modal-${entityType}`;
    if (searchTimeouts[key]) clearTimeout(searchTimeouts[key]);
    const timeoutId = setTimeout(() => performEntitySearchForModal(value, entityType), 300);
    setSearchTimeouts(prev => ({ ...prev, [key]: timeoutId }));
  };

  // Search entities for modal
  const performEntitySearchForModal = async (term, entityType) => {
    try {
      let endpoint = '';
      switch (entityType) {
        case 'Customer':
          endpoint = !term || term.trim() === '' ? `/api/customers?limit=10` : `/api/customers?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'Opportunity':
          endpoint = !term || term.trim() === '' ? `/api/opportunities/for-assignment?limit=10` : `/api/opportunities/for-assignment?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'Activity':
          endpoint = !term || term.trim() === '' ? `/api/activities?limit=10` : `/api/activities?search=${encodeURIComponent(term)}&limit=15`;
          break;
        case 'SaleNote':
          endpoint = !term || term.trim() === '' ? `/api/sales-notes?limit=10` : `/api/sales-notes?search=${encodeURIComponent(term)}&limit=15`;
          break;
        default:
          return;
      }

      console.log(`🔍 LeadForm: Searching ${entityType} with endpoint:`, endpoint);
      const response = await fetch(endpoint, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } });
      console.log(`🔍 LeadForm: ${entityType} search response status:`, response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`🔍 LeadForm: ${entityType} search response data:`, data);
        
        let results = [];
        const key = entityType.toLowerCase() + 's';
        if (data[key]) results = data[key];
        else if (data.saleNotes) results = data.saleNotes;
        else if (data.salesNotes) results = data.salesNotes;
        else if (data.activities) results = data.activities;
        else if (data.opportunities) results = data.opportunities;
        else if (data.data && Array.isArray(data.data)) results = data.data;
        else if (Array.isArray(data)) results = data;

        console.log(`🔍 LeadForm: ${entityType} search results count:`, results.length, 'results:', results);
        setEntitySelectionModal(prev => ({ ...prev, results, loading: false }));
      } else {
        console.error(`🔍 LeadForm: ${entityType} search failed with status:`, response.status);
        setEntitySelectionModal(prev => ({ ...prev, results: [], loading: false }));
      }
    } catch (err) {
      console.error(`🔍 LeadForm: ${entityType} search error:`, err);
      setEntitySelectionModal(prev => ({ ...prev, results: [], loading: false }));
    }
  };

  const [qualificationOptions, setQualificationOptions] = useState([
    { value: 'Cold', label: t('leads.qualificationCold', 'Cold') },
    { value: 'Warm', label: t('leads.qualificationWarm', 'Warm') },
    { value: 'Hot', label: t('leads.qualificationHot', 'Hot') },
    { value: 'Qualified', label: t('leads.qualificationQualified', 'Qualified') },
  ]);
  const [currencyOptions, setCurrencyOptions] = useState([
    { value: 'THB', label: 'THB (฿)' },
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'JPY', label: 'JPY (¥)' },
  ]);

  // Helper function to check if field is hidden
  const isFieldHidden = (fieldName) => {
    return hiddenFields.includes(fieldName);
  };

  // Show loading state while config is loading
  if (configLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col justify-center items-center h-64 space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 dark:border-teal-400"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading configuration...</p>
        </div>
      </div>
    );
  }

  // Show error state if config failed to load
  if (configError) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">Configuration Error</h3>
          <p className="text-red-600 dark:text-red-400">{configError}</p>
          <p className="text-sm text-red-500 dark:text-red-400 mt-2">
            Unable to load entity configuration. Please try refreshing the page or contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (typeof onCancel === 'function') {
                onCancel();
              } else {
                // Fallback: navigate back to leads list or lead detail
                const leadId = lead?.LeadID || params?.id;
                if (leadId) {
                  navigate(`/leads/${leadId}`);
                } else {
                  navigate('/leads');
                }
              }
            }}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-4 w-4 dark:text-gray-300" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {mode === 'create' ? t('leads.createLead') : t('leads.editLead')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {mode === 'create' 
                ? t('leads.createSubtitle', 'Add a new lead to track potential opportunities') 
                : t('leads.editSubtitle', 'Update lead information and status')
              }
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowHelpModal(true)}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            title="ดูคู่มือการใช้งาน"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>{loading ? t('common.saving') : t('common.save')}</span>
        </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Validation Summary */}
        <ValidationSummary 
          errors={errors} 
          onFieldClick={(field) => {
            const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus?.();
            }
          }}
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            {(() => {
              const hasAnyField = !isFieldHidden('Title') || !isFieldHidden('LeadSource') || 
                                 !isFieldHidden('Status') || !isFieldHidden('Priority') || true; // Always show for SalespersonID
              if (!hasAnyField) return null;
              
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Target className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-lg font-medium dark:text-white">{t('leads.basicInfo', 'Basic Information')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {!isFieldHidden('Title') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.title')}
                        </label>
                        {getSuggestionConfig(entityConfig, 'Title') ? (
                          <AutocompleteTextField
                            entityName="lead"
                            fieldName="Title"
                            value={formData.Title}
                            onChange={(e) => handleFieldChange('Title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.titlePlaceholder', 'e.g., Enterprise Software Solution')}
                            suggestConfig={getSuggestionConfig(entityConfig, 'Title')}
                          />
                        ) : (
                          <input
                            type="text"
                            value={formData.Title}
                            onChange={(e) => handleFieldChange('Title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.titlePlaceholder', 'e.g., Enterprise Software Solution')}
                          />
                        )}
                      </div>
                    )}

                    {!isFieldHidden('LeadSource') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.leadSource')}
                        </label>
                        {getSuggestionConfig(entityConfig, 'LeadSource') ? (
                          <AutocompleteTextField
                            entityName="lead"
                            fieldName="LeadSource"
                            value={formData.LeadSource}
                            onChange={(e) => handleFieldChange('LeadSource', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.leadSourcePlaceholder', 'e.g., Website, Referral, Cold Call')}
                            suggestConfig={getSuggestionConfig(entityConfig, 'LeadSource')}
                          />
                        ) : (
                          <input
                            type="text"
                            value={formData.LeadSource}
                            onChange={(e) => handleFieldChange('LeadSource', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.leadSourcePlaceholder', 'e.g., Website, Referral, Cold Call')}
                          />
                        )}
                      </div>
                    )}

                    {!isFieldHidden('Status') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.status')} *
                        </label>
                        <select
                          value={formData.Status}
                          onChange={(e) => handleFieldChange('Status', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {!isFieldHidden('Priority') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.priority')} *
                        </label>
                        <select
                          value={formData.Priority}
                          onChange={(e) => handleFieldChange('Priority', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                        >
                          {priorityOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('leads.salesperson', 'Salesperson')}
                      </label>
                      <select
                        value={formData.SalespersonID}
                        onChange={(e) => handleFieldChange('SalespersonID', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">{t('leads.selectSalesperson', 'Select a salesperson')}</option>
                        {salespersons.length === 0 ? (
                          <option value="" disabled>{t('leads.noSalespersons', 'No salespersons available')}</option>
                        ) : (
                          salespersons.map(salesperson => (
                            <option key={salesperson.UserID} value={String(salesperson.UserID)}>
                              {salesperson.FirstName} {salesperson.LastName}
                            </option>
                          ))
                        )}
                      </select>
                      {showSalespersonWarning && (
                        <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start space-x-2">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                              {t('leads.salespersonChangeWarning', 'Warning: Changing the salesperson')}
                            </p>
                            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                              {t('leads.salespersonChangeWarningDetail', 'Under the current data governance policy, you may no longer be able to view or update this record after saving.')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Contact Information */}
            {(() => {
              const hasAnyField = !isFieldHidden('ContactName') || !isFieldHidden('JobTitle') || 
                                 !isFieldHidden('ContactEmail') || !isFieldHidden('ContactPhone');
              if (!hasAnyField) return null;
              
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      <h3 className="text-lg font-medium dark:text-white">{t('leads.contactInfo', 'Contact Information')}</h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCustomerPopup(true)}
                      className="flex items-center space-x-2 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm"
                    >
                      <Users className="h-4 w-4" />
                      <span>{t('leads.pickFromCustomer', 'Pick from Customer')}</span>
                    </button>
                  </div>
                  
                  {errors.contact && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.contact}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {!isFieldHidden('ContactName') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.contactName')}
                        </label>
                        <input
                          type="text"
                          value={formData.ContactName}
                          onChange={(e) => handleFieldChange('ContactName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                          placeholder={t('leads.contactNamePlaceholder', 'e.g., John Smith')}
                        />
                      </div>
                    )}

                    {!isFieldHidden('JobTitle') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.jobTitle')}
                        </label>
                        {getSuggestionConfig(entityConfig, 'JobTitle') ? (
                          <AutocompleteTextField
                            entityName="lead"
                            fieldName="JobTitle"
                            value={formData.JobTitle}
                            onChange={(e) => handleFieldChange('JobTitle', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.jobTitlePlaceholder', 'e.g., IT Director')}
                            suggestConfig={getSuggestionConfig(entityConfig, 'JobTitle')}
                          />
                        ) : (
                          <input
                            type="text"
                            value={formData.JobTitle}
                            onChange={(e) => handleFieldChange('JobTitle', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.jobTitlePlaceholder', 'e.g., IT Director')}
                          />
                        )}
                      </div>
                    )}

                    {!isFieldHidden('ContactEmail') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.email')}
                        </label>
                        <input
                          type="email"
                          id="ContactEmail"
                          name="ContactEmail"
                          value={formData.ContactEmail}
                          onChange={(e) => handleFieldChange('ContactEmail', e.target.value)}
                          onBlur={(e) => handleFieldChange('ContactEmail', normalizeEmailList(e.target.value))}
                          className={getInputClassName(!!errors.ContactEmail)}
                          placeholder={t('leads.emailPlaceholder', 'e.g., john@company.com')}
                        />
                        <FieldError error={errors.ContactEmail} />
                      </div>
                    )}

                    {!isFieldHidden('ContactPhone') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.phone')}
                        </label>
                        <input
                          type="tel"
                          value={formData.ContactPhone}
                          onChange={(e) => handleFieldChange('ContactPhone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                          placeholder={t('leads.phonePlaceholder', 'e.g., +1 (555) 123-4567')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Company Information */}
            {(() => {
              const hasAnyField = !isFieldHidden('CompanyName') || !isFieldHidden('Industry') || 
                                 !isFieldHidden('Website');
              if (!hasAnyField) return null;
              
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-lg font-medium dark:text-white">{t('leads.companyInfo', 'Company Information')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {!isFieldHidden('CompanyName') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.companyName')}
                        </label>
                        {getSuggestionConfig(entityConfig, 'CompanyName') ? (
                          <AutocompleteTextField
                            entityName="lead"
                            fieldName="CompanyName"
                            value={formData.CompanyName}
                            onChange={(e) => handleFieldChange('CompanyName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.companyNamePlaceholder', 'e.g., Acme Corporation')}
                            suggestConfig={getSuggestionConfig(entityConfig, 'CompanyName')}
                          />
                        ) : (
                          <input
                            type="text"
                            value={formData.CompanyName}
                            onChange={(e) => handleFieldChange('CompanyName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.companyNamePlaceholder', 'e.g., Acme Corporation')}
                          />
                        )}
                      </div>
                    )}

                    {!isFieldHidden('Industry') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.industry')}
                        </label>
                        {getSuggestionConfig(entityConfig, 'Industry') ? (
                          <AutocompleteTextField
                            entityName="lead"
                            fieldName="Industry"
                            value={formData.Industry}
                            onChange={(e) => handleFieldChange('Industry', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.industryPlaceholder', 'e.g., Technology, Healthcare')}
                            suggestConfig={getSuggestionConfig(entityConfig, 'Industry')}
                          />
                        ) : (
                          <input
                            type="text"
                            value={formData.Industry}
                            onChange={(e) => handleFieldChange('Industry', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                            placeholder={t('leads.industryPlaceholder', 'e.g., Technology, Healthcare')}
                          />
                        )}
                      </div>
                    )}

                    {!isFieldHidden('Website') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.website')}
                        </label>
                        <input
                          type="url"
                          id="Website"
                          name="Website"
                          value={formData.Website}
                          onChange={(e) => handleFieldChange('Website', e.target.value)}
                          className={getInputClassName(!!errors.Website)}
                          placeholder={t('leads.websitePlaceholder', 'e.g., https://www.company.com')}
                        />
                        <FieldError error={errors.Website} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Description and Notes */}
            {(() => {
              const hasAnyField = !isFieldHidden('Description') || !isFieldHidden('Notes');
              if (!hasAnyField) return null;
              
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-lg font-medium dark:text-white">{t('leads.descriptionNotes', 'Description & Notes')}</h3>
                  </div>
                  
                  <div className="space-y-6">
                    {!isFieldHidden('Description') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.description')}
                        </label>
                        <textarea
                          value={formData.Description}
                          onChange={(e) => handleFieldChange('Description', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                          placeholder={t('leads.descriptionPlaceholder', 'Describe the lead opportunity...')}
                        />
                      </div>
                    )}

                    {!isFieldHidden('Notes') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.notes')}
                        </label>
                        <textarea
                          value={formData.Notes}
                          onChange={(e) => handleFieldChange('Notes', e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                          placeholder={t('leads.notesPlaceholder', 'Add any additional notes or observations...')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <CustomAttributesEditor
                fields={customAttributeFields}
                attributes={attributes}
                onChange={(next) => setAttributes(next)}
                onAddField={handleAddCustomAttributeField}
                title={t(
                  'leads.additionalDataSection',
                  'Lead Additional Data'
                )}
                allExistingFieldNames={entityConfig?.config?.fields ? Object.keys(entityConfig.config.fields) : []}
                entityName="lead"
                onFieldsUpdated={async () => {
                  // Clear config cache and trigger reload
                  clearConfigCache('lead');
                  window.dispatchEvent(new CustomEvent('entity-config-updated', { 
                    detail: { entityName: 'lead' } 
                  }));
                }}
              />
            </div>

            {/* Attachments (Edit Mode Only) */}
            {mode === 'edit' && lead && lead.LeadID && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Paperclip className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium dark:text-white">{t('leads.attachments', 'Attachments')}</h3>
                </div>
                
                <AttachmentServiceV2 
                  entityType="Lead" 
                  entityId={lead.LeadID} 
                  onAttachmentChange={() => {
                    // Optional: Add any callback logic here
                  }}
                />
              </div>
            )}
            
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Related Entities - moved to top of sidebar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Link className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium dark:text-white">{t('activities.relatedEntities', 'Relationships')}</h3>
                </div>
                {relatedEntities.length > 0 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {relatedEntities.length} {relatedEntities.length === 1 ? 'entity' : 'entities'} selected
                  </span>
                )}
              </div>

              {/* Entity Type Cards */}
              <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-4">
                {entityTypes.map(type => {
                  const count = relatedEntities.filter(e => e.type === type.value).length;
                  const icon = () => {
                    switch (type.value) {
                      case 'Customer': return <Building className="h-5 w-5" />;
                      case 'Opportunity': return <Target className="h-5 w-5" />;
                      case 'Activity': return <Calendar className="h-5 w-5" />;
                      case 'SaleNote': return <FileText className="h-5 w-5" />;
                      default: return <FileText className="h-5 w-5" />;
                    }
                  };
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => openEntitySelectionModal(type.value, '')}
                      className={`group relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${
                        count > 0
                          ? 'border-teal-500 dark:border-teal-400 bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-teal-400 dark:hover:border-teal-500 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      <div className={`mb-2 ${count > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-teal-500'}`}>{icon()}</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white text-center">{type.label}</div>
                      {count > 0 && (
                        <div className="absolute -top-2 -right-2 bg-teal-500 dark:bg-teal-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">{count}</div>
                      )}
                      <Plus className={`absolute bottom-1 right-1 h-4 w-4 ${count > 0 ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'} text-gray-400 dark:text-gray-500`} />
                    </button>
                  );
                })}
              </div>

              {/* Multiple Customers Warning */}
              {relatedEntities.filter(e => e.type === 'Customer').length > 1 && (
                <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300 flex-1">
                      มีลูกค้าหลายราย / Multiple Customers
                    </p>
                    <div className="relative group">
                      <HelpCircle className="h-4 w-4 text-amber-500 dark:text-amber-400 cursor-help" />
                      <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <p className="mb-2">
                          <strong>TH:</strong> ลูกค้าที่ตรงกับข้อมูลผู้ติดต่อในฟอร์มจะแสดงเป็น "หลัก" ข้อมูลผู้ติดต่อ/บริษัทของ Lead สามารถมาจากลูกค้าได้เพียงรายเดียว
                        </p>
                        <p>
                          <strong>EN:</strong> Customer matching the contact info in the form is shown as "Primary". Lead's contact/company info can only come from one customer.
                        </p>
                        <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected Entities */}
              {relatedEntities.length > 0 && (
                <div className="space-y-3">
                  {entityTypes.map(type => {
                    const items = relatedEntities.filter(e => e.type === type.value);
                    if (items.length === 0) return null;
                    const iconSmall = () => {
                      switch (type.value) {
                        case 'Customer': return <Building className="h-4 w-4" />;
                        case 'Opportunity': return <Target className="h-4 w-4" />;
                        case 'Activity': return <Calendar className="h-4 w-4" />;
                        case 'SaleNote': return <FileText className="h-4 w-4" />;
                        default: return <FileText className="h-4 w-4" />;
                      }
                    };
                    
                    // Check if this is a Customer type and there are multiple customers
                    const isMultipleCustomers = type.value === 'Customer' && items.length > 1;
                    
                    // Find primary customer - the one matching current contact info
                    const findPrimaryCustomerIndex = () => {
                      if (!isMultipleCustomers) return -1;
                      const contactName = (formData.ContactName || '').trim().toLowerCase();
                      const contactEmail = (formData.ContactEmail || '').trim().toLowerCase();
                      
                      // Try to match by name or email
                      for (let i = 0; i < items.length; i++) {
                        const entityName = (items[i].name || '').toLowerCase();
                        // Check if customer name contains contact name or vice versa
                        if (contactName && entityName && (
                          entityName.includes(contactName) || 
                          contactName.includes(entityName.split(' ')[0]) // Match first name
                        )) {
                          return i;
                        }
                      }
                      // No match found - return -1 (no primary)
                      return -1;
                    };
                    
                    const primaryIndex = findPrimaryCustomerIndex();
                    
                    return (
                      <div key={type.value} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          {iconSmall()}
                          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{type.label} ({items.length})</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {items.map((entity, idx) => {
                            const isPrimary = type.value === 'Customer' && isMultipleCustomers && idx === primaryIndex;
                            
                            return (
                              <div 
                                key={`${entity.type}-${entity.id}-${idx}`} 
                                className={`group flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                                  isPrimary
                                    ? 'bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-300 dark:border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                                    : 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 hover:bg-teal-100 dark:hover:bg-teal-900/30'
                                }`}
                              >
                                {iconSmall()}
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{entity.name || `${type.value} ${entity.id}`}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">#{entity.id}</span>
                                {isPrimary && (
                                  <span 
                                    className="px-1.5 py-0.5 text-xs font-medium bg-amber-200 dark:bg-amber-700 text-amber-800 dark:text-amber-200 rounded cursor-help"
                                    title="ตรงกับข้อมูลผู้ติดต่อ / Matches contact info"
                                  >
                                    หลัก/Primary
                                  </span>
                                )}
                                <button type="button" onClick={() => setRelatedEntities(prev => prev.filter(e => !(e.type === entity.type && e.id === entity.id)))} className="ml-1 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" title={`Remove ${entity.name}`}>
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            );
                          })}
                          <button type="button" onClick={() => openEntitySelectionModal(type.value, '')} className="flex items-center space-x-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 text-sm font-medium transition-colors">
                            <Plus className="h-3 w-3" />
                            <span>Add {type.label}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Scoring and Qualification */}
            {(() => {
              const hasAnyField = !isFieldHidden('Score') || !isFieldHidden('QualificationLevel');
              if (!hasAnyField) return null;
              
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Star className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-lg font-medium dark:text-white">{t('leads.scoringQualification', 'Scoring & Qualification')}</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {!isFieldHidden('Score') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.score')} (0-100)
                        </label>
                        <input
                          type="number"
                          id="Score"
                          name="Score"
                          min="0"
                          max="100"
                          value={formData.Score}
                          onChange={(e) => handleFieldChange('Score', e.target.value)}
                          className={getInputClassName(!!errors.Score)}
                          placeholder="85"
                        />
                        <FieldError error={errors.Score} />
                      </div>
                    )}

                    {!isFieldHidden('QualificationLevel') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.qualificationLevel')}
                        </label>
                        <select
                          value={formData.QualificationLevel}
                          onChange={(e) => handleFieldChange('QualificationLevel', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="">{t('leads.selectQualification', 'Select qualification level')}</option>
                          {qualificationOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Financial Information */}
            {(() => {
              const hasAnyField = !isFieldHidden('EstimatedBudget') || !isFieldHidden('Currency');
              if (!hasAnyField) return null;
              
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-lg font-medium dark:text-white">{t('leads.financialInfo', 'Financial Information')}</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {!isFieldHidden('EstimatedBudget') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.estimatedBudget')}
                        </label>
                        <input
                          type="text"
                          id="EstimatedBudget"
                          name="EstimatedBudget"
                          value={formatNumberWithCommas(formData.EstimatedBudget)}
                          onChange={(e) => {
                            const parsedValue = parseNumberFromFormatted(e.target.value);
                            handleFieldChange('EstimatedBudget', parsedValue);
                          }}
                          onBlur={(e) => {
                            // Ensure value is properly formatted on blur
                            const parsedValue = parseNumberFromFormatted(e.target.value);
                            if (parsedValue !== '') {
                              handleFieldChange('EstimatedBudget', parsedValue);
                            }
                          }}
                          className={getInputClassName(!!errors.EstimatedBudget)}
                          placeholder="50,000"
                        />
                        <FieldError error={errors.EstimatedBudget} />
                      </div>
                    )}

                    {!isFieldHidden('Currency') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.currency')}
                        </label>
                        <select
                          value={formData.Currency}
                          onChange={(e) => handleFieldChange('Currency', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                        >
                          {currencyOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Timeline */}
            {(() => {
              const hasAnyField = !isFieldHidden('ExpectedCloseDate') || !isFieldHidden('LastContactDate') || !isFieldHidden('NextFollowUpDate');
              if (!hasAnyField) return null;
              
              return (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-lg font-medium dark:text-white">{t('leads.timeline', 'Timeline')}</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {!isFieldHidden('ExpectedCloseDate') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.expectedCloseDate')}
                        </label>
                        <input
                          type="date"
                          id="ExpectedCloseDate"
                          name="ExpectedCloseDate"
                          value={formData.ExpectedCloseDate}
                          onChange={(e) => handleFieldChange('ExpectedCloseDate', e.target.value)}
                          className={getInputClassName(!!errors.ExpectedCloseDate)}
                        />
                        <FieldError error={errors.ExpectedCloseDate} />
                      </div>
                    )}

                    {!isFieldHidden('LastContactDate') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.lastContactDate')}
                        </label>
                        <input
                          type="date"
                          value={formData.LastContactDate}
                          onChange={(e) => handleFieldChange('LastContactDate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    )}

                    {!isFieldHidden('NextFollowUpDate') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('leads.nextFollowUpDate')}
                        </label>
                        <input
                          type="date"
                          value={formData.NextFollowUpDate}
                          onChange={(e) => handleFieldChange('NextFollowUpDate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Conversion Status (Edit Mode Only) */}
            {mode === 'edit' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Award className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium dark:text-white">{t('leads.conversionStatus', 'Conversion Status')}</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="convertedToOpportunity"
                      checked={formData.ConvertedToOpportunity}
                      onChange={(e) => handleFieldChange('ConvertedToOpportunity', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-teal-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                    />
                    <label htmlFor="convertedToOpportunity" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                      {t('leads.convertedToOpportunity')}
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="convertedToCustomer"
                      checked={formData.ConvertedToCustomer}
                      onChange={(e) => handleFieldChange('ConvertedToCustomer', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-teal-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                    />
                    <label htmlFor="convertedToCustomer" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                      {t('leads.convertedToCustomer')}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          {mode === 'edit' && lead?.LeadID && (
            <div className="space-y-6">
              {/* Tags Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Tag className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-lg font-medium dark:text-white">{t('common.tags', 'Tags')}</h3>
                </div>
                <TagService
                  entityType="Lead"
                  entityId={lead.LeadID}
                  initialTags={lead.Tags ? (typeof lead.Tags === 'string' ? lead.Tags.split(',').map(t => t.trim()).filter(Boolean) : lead.Tags) : []}
                  maxTags={15}
                  onChange={async (newTags) => {
                    // Tags updated successfully
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="h-24 md:h-32" />

        <div className="fixed bottom-6 right-6 lg:right-[max(1.5rem,calc((100vw-72rem)/2))] z-40 flex flex-col-reverse gap-3 md:flex-row">
          <button
            type="button"
            onClick={() => {
              if (typeof onCancel === 'function') {
                onCancel();
              } else {
                const leadId = lead?.LeadID || params?.id;
                if (leadId) {
                  navigate(`/leads/${leadId}`);
                } else {
                  navigate('/leads');
                }
              }
            }}
            className="inline-flex items-center space-x-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/80 px-5 py-2 shadow-lg shadow-gray-500/10 dark:shadow-black/30 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 transition-colors backdrop-blur"
          >
            <X className="h-4 w-4" />
            <span>{t('common.cancel')}</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center space-x-2 rounded-full bg-teal-600 dark:bg-teal-500 px-6 py-2 shadow-lg shadow-teal-500/30 dark:shadow-teal-900/30 text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-60 transition-colors"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{loading ? t('common.saving') : t('common.save')}</span>
          </button>
        </div>
      </form>

      {/* Entity Selection Modal */}
      <EntitySelectionModal
        isOpen={entitySelectionModal.isOpen}
        entityType={entitySelectionModal.entityType}
        title={`Select ${entitySelectionModal.entityType || 'Entity'}`}
        subtitle={entitySelectionModal.selectedIds.length > 0 ? `${entitySelectionModal.selectedIds.length} ${t('activities.selected', 'selected')}` : undefined}
        searchTerm={entitySelectionModal.searchTerm}
        onSearchTermChange={handleEntityModalSearchChange}
        searchPlaceholder={t('activities.searchEntity', { entityType: entitySelectionModal.entityType })}
        loading={entitySelectionModal.loading}
        results={entitySelectionModal.results}
        selectedIds={entitySelectionModal.selectedIds}
        onToggleEntity={toggleEntitySelectionInModal}
        onClose={() => closeEntitySelectionModal(false)}
        onConfirm={() => closeEntitySelectionModal(true)}
        confirmLabel={
          entitySelectionModal.selectedIds.length > 0
            ? `${t('common.add', 'Add')} (${entitySelectionModal.selectedIds.length})`
            : t('common.add', 'Add')
        }
        confirmIcon={Plus}
        confirmDisabled={entitySelectionModal.selectedIds.length === 0}
        cancelLabel={t('common.cancel', 'Cancel')}
        emptyMessage={t('activities.noResultsFound', 'No results found')}
        idleMessage={t('activities.startTypingToSearch', 'Start typing to search')}
        loadingMessage={t('activities.searching', 'Searching...')}
        resolveId={(entity, type) => getEntityId(entity, type || entitySelectionModal.entityType)}
        resolveName={(entity, type) => getEntityName(entity, type || entitySelectionModal.entityType)}
        resolveSubInfo={(entity, type) => getEntitySubInfo(entity, type || entitySelectionModal.entityType)}
      />
      {/* Customer Selection Popup */}
      <CustomerSelectionPopup
        isOpen={showCustomerPopup}
        onClose={() => setShowCustomerPopup(false)}
        onCustomerSelect={handleCustomerSelect}
      />
      {/* Help Modal */}
      <HelpModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
        title="คู่มือการจัดการ Leads"
        content={leadHelpContent}
      />

      {/* Opportunity Sync Dialog */}
      {showOpportunitySyncDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <RefreshCw className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('leads.syncOpportunityTitle', 'ซิงค์ข้อมูลกับโอกาส / Sync with Opportunity')}
                </h3>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('leads.syncOpportunityMessage', 
                  'ต้องการอัปเดตข้อมูลเดียวกันในโอกาสทางการขายที่เชื่อมโยงด้วยหรือไม่?'
                )}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('leads.syncOpportunityMessageEn', 
                  'Would you like to update the same fields in the linked opportunity?'
                )}
              </p>
              
              {/* Fields to sync tooltip */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('leads.fieldsToSync', 'ข้อมูลที่จะซิงค์ / Fields to sync:')}
                </p>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• {t('leads.contactInfo', 'ข้อมูลผู้ติดต่อ / Contact Information')}</li>
                  <li>• {t('leads.companyInfo', 'ข้อมูลบริษัท / Company Information')}</li>
                  <li>• {t('leads.estimatedBudget', 'งบประมาณประมาณการ / Estimated Budget')}</li>
                  <li>• {t('leads.customFields', 'ฟิลด์กำหนดเอง / Custom Fields')}</li>
                </ul>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end space-x-3 p-5 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleSkipSync}
                disabled={syncingOpportunity}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                {t('leads.skipSync', 'ข้าม / Skip')}
              </button>
              <button
                type="button"
                onClick={handleSyncOpportunity}
                disabled={syncingOpportunity}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2 transition-colors"
              >
                {syncingOpportunity ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t('common.syncing', 'กำลังซิงค์... / Syncing...')}</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    <span>{t('leads.syncNow', 'ซิงค์เลย / Sync Now')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadForm; 
